import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { Button } from '@oysterjs/ui/button';
import { DialogActions, DialogBody, DialogTitle, MountableDialog } from '@oysterjs/ui/dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu
} from '@oysterjs/ui/dropdown';

import {
  Action,
  AmsRefKind,
  EmailAmsMatchOutcome,
  Resource,
  type EmailAmsFilingTargetInput
} from '~/api/graphql';
import { AccountView, FolderView, ActivityView } from '~/components/ams-picker/ams-browser-record';
import { NO_ACCOUNT_FILTERS } from '~/components/ams-picker/ams-browser-model';
import { SearchRail } from '~/components/ams-picker/picker-ui';
import { usePermissions } from '~/components/permissions';
import type { EmailAmsMatch } from '~/components/workspaces/hooks/use-email-ams-match';
import { integrationName } from '~/lib/integrations';

import { type ChosenClient, ClientList, type ClientOption, refInputOf } from './client-list';
import {
  type ClientSearchScope,
  ClientSearchResults,
  importSearchedClient,
  useClientSearch
} from './client-search';

export type { ChosenClient } from './client-list';

/**
 * compose saves the choice on a draft before it sends, searching through its workspace;
 * push hands it on to the Push confirm, searching through the sent email, so it offers
 * no way to skip logging.
 */
export type ChooseClientFor =
  | {
      mode: 'compose';
      workspaceId: string;
      /** The choice not to log the email. */
      onSkip?: () => void;
    }
  | { mode: 'push'; outboundEmailId: string };

const optionsOfMatch = (match: EmailAmsMatch): ClientOption[] =>
  match.candidates.map((candidate) => ({
    target: refInputOf(candidate.account),
    displayName: candidate.displayName,
    detail:
      match.outcome === EmailAmsMatchOutcome.RecipientsMatchSeveral
        ? candidate.matchedAddresses.map((matched) => matched.address).join(', ')
        : undefined,
    isSearched: false
  }));

/**
 * Picks the one client an email is logged to: among the clients its recipients match,
 * or by searching the AMS. Mount it inside an AnimatePresence.
 */
const ChooseClientDialogContent = (
  props: ChooseClientFor & {
    match: EmailAmsMatch;
    toAddresses: string[];
    /** The client the email is logged to now, picked when the dialog opens. */
    currentClient?: ChosenClient | null;
    /** Resolves to a message saying why the choice wasn't taken, or null to close. */
    onChoose: (client: ChosenClient) => Promise<string | null>;
    onClose: () => void;
    contentOnly?: boolean;
    onChoosingChange?: (isChoosing: boolean) => void;
  }
) => {
  const { match, mode } = props;
  const amsName = integrationName(match.integrationType);
  const { currentClient } = props;
  const canSearchClients = usePermissions().hasPermission(Resource.Client, Action.List);
  const matchedOptions = optionsOfMatch(match);
  // A client chosen earlier by search is listed too, so reopening shows it picked.
  const isCurrentClientUnmatched =
    !!currentClient &&
    !matchedOptions.some((option) => option.target.externalId === currentClient.target.externalId);
  const listedOptions = isCurrentClientUnmatched
    ? [...matchedOptions, { ...currentClient, isSearched: false }]
    : matchedOptions;
  const listedKeys = new Set(listedOptions.map((option) => option.target.externalId));
  const isNoMatch = match.outcome === EmailAmsMatchOutcome.NoMatch;

  const [isSearchShown, setIsSearchShown] = React.useState(
    isNoMatch || match.outcome === EmailAmsMatchOutcome.OneMatch
  );
  const [query, setQuery] = React.useState('');
  const searchScope: ClientSearchScope =
    props.mode === 'compose'
      ? { kind: 'workspace', id: props.workspaceId }
      : { kind: 'outboundEmail', id: props.outboundEmailId };
  const search = useClientSearch(searchScope, query);
  const [picked, setPicked] = React.useState<ClientOption | undefined>(() =>
    listedOptions.find((option) => option.target.externalId === currentClient?.target.externalId)
  );
  const [filingTarget, setFilingTarget] = React.useState<EmailAmsFilingTargetInput | null>(
    currentClient?.filingTarget ?? null
  );
  const [shownRecords, setShownRecords] = React.useState<
    'policies' | 'folders' | 'activities' | null
  >(null);
  const [policyFilters, setPolicyFilters] = React.useState(NO_ACCOUNT_FILTERS);
  const canBrowsePolicies = usePermissions().hasPermission(Resource.BusinessPolicy, Action.List);
  const [isChoosing, setIsChoosing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const pick = (option: ClientOption) => {
    setPicked(option);
    setFilingTarget(
      option.target.externalId === currentClient?.target.externalId
        ? (currentClient.filingTarget ?? null)
        : null
    );
    setShownRecords(null);
    setPolicyFilters(NO_ACCOUNT_FILTERS);
    setError(null);
  };

  // A pick from the results of an earlier query is no longer on screen to confirm.
  const changeQuery = (next: string) => {
    setQuery(next);
    if (picked?.isSearched) setPicked(undefined);
  };

  // Closing mid-save would let the save land after the agent moved on.
  const close = isChoosing ? () => {} : props.onClose;

  const choose = async () => {
    if (!picked) return;
    setIsChoosing(true);
    props.onChoosingChange?.(true);
    setError(null);
    const known = picked.isSearched
      ? await importSearchedClient(searchScope, picked, amsName)
      : picked;
    const refusal =
      typeof known === 'string'
        ? known
        : await props.onChoose({
            target: known.target,
            displayName: known.displayName,
            ...(filingTarget ? { filingTarget } : {})
          });
    props.onChoosingChange?.(false);
    if (!isMounted.current) return;
    setIsChoosing(false);
    if (refusal) {
      setError(refusal);
    } else {
      props.onClose();
    }
  };

  const firstAddress = match.candidates[0]?.matchedAddresses[0]?.address;
  const intro = (): React.ReactNode => {
    switch (match.outcome) {
      case EmailAmsMatchOutcome.OneMatch:
        return (
          <>
            Cara matched <strong>{firstAddress}</strong> to this client. Pick a different one, or
            keep it.
          </>
        );
      case EmailAmsMatchOutcome.SeveralMatch:
        return (
          <>
            {match.candidates.length} clients in {amsName} use <strong>{firstAddress}</strong>. Pick
            the one this email is about.
          </>
        );
      case EmailAmsMatchOutcome.RecipientsMatchSeveral:
        return (
          <>
            The recipients of this email match {match.candidates.length} clients in {amsName}. Pick
            the one it&apos;s about.
          </>
        );
      case EmailAmsMatchOutcome.NoMatch:
        return (
          <>
            No client in {amsName} uses <strong>{props.toAddresses.join(', ')}</strong>.
            {canSearchClients && ' Search by name to pick one.'}
          </>
        );
    }
  };

  const primaryDestination =
    filingTarget?.ref.kind === AmsRefKind.Policy
      ? `Policy · ${filingTarget.displayName}`
      : filingTarget?.ref.kind === AmsRefKind.Activity
        ? `Activity · ${filingTarget.displayName}`
        : 'Client record';
  const folderName =
    filingTarget?.ref.kind === AmsRefKind.Folder
      ? filingTarget.displayName
      : filingTarget?.folderDisplayName;
  const hasFolder =
    !!filingTarget && (filingTarget.ref.kind === AmsRefKind.Folder || !!filingTarget.folder);

  const content = (
    <>
      <DialogTitle className="shrink-0">Choose a {amsName} client</DialogTitle>
      <DialogBody className="flex min-h-0 flex-1 flex-col gap-4 text-sm/6 text-neutral-600 dark:text-neutral-300">
        <div className="shrink-0 space-y-4">
          <div>{intro()}</div>
          {!canSearchClients ? null : isSearchShown ? (
            <SearchRail
              query={query}
              onQueryChange={changeQuery}
              placeholder={`Search ${amsName} clients by name`}
              ariaLabel={`Search ${amsName} clients by name`}
            />
          ) : (
            <button
              type="button"
              className="font-medium text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"
              onClick={() => setIsSearchShown(true)}
            >
              None of these? Search {amsName} clients
            </button>
          )}
        </div>
        <div
          role="region"
          aria-label="Clients and filing locations"
          tabIndex={0}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto"
        >
          {listedOptions.length > 0 && (
            <ClientList
              label={`Clients the recipients match in ${amsName}`}
              options={listedOptions}
              pickedKey={picked?.target.externalId}
              onPick={pick}
            />
          )}
          {canSearchClients && isSearchShown && (
            <ClientSearchResults
              amsName={amsName}
              query={query}
              search={search}
              listedKeys={listedKeys}
              pickedKey={picked?.target.externalId}
              onPick={pick}
            />
          )}
          {picked && (match.filingKinds?.length ?? 0) > 0 && canBrowsePolicies && (
            <div className="space-y-3">
              <Dropdown>
                <DropdownButton outline disabled={isChoosing} className="max-w-full">
                  <span className="shrink-0">File under</span>
                  <span className="min-w-0 truncate" title={primaryDestination}>
                    {primaryDestination}
                  </span>
                  <ChevronDownIcon data-slot="icon" className="shrink-0" />
                </DropdownButton>
                <DropdownMenu anchor="bottom start" className="z-50 max-w-sm">
                  <DropdownItem
                    onClick={() => {
                      setFilingTarget(null);
                      setShownRecords(null);
                    }}
                  >
                    Client record
                  </DropdownItem>
                  {match.filingKinds.includes(AmsRefKind.Policy) && (
                    <DropdownItem onClick={() => setShownRecords('policies')}>
                      Choose policy…
                    </DropdownItem>
                  )}
                  {match.filingKinds.includes(AmsRefKind.Activity) && (
                    <DropdownItem onClick={() => setShownRecords('activities')}>
                      Choose activity…
                    </DropdownItem>
                  )}
                  {(match.filingKinds.includes(AmsRefKind.Folder) || hasFolder) && (
                    <DropdownDivider />
                  )}
                  {match.filingKinds.includes(AmsRefKind.Folder) && (
                    <DropdownItem onClick={() => setShownRecords('folders')}>
                      {hasFolder ? 'Change folder…' : 'Choose folder…'}
                    </DropdownItem>
                  )}
                  {hasFolder && (
                    <DropdownItem
                      onClick={() => {
                        setFilingTarget(
                          filingTarget.ref.kind === AmsRefKind.Folder
                            ? null
                            : { ref: filingTarget.ref, displayName: filingTarget.displayName }
                        );
                        setShownRecords(null);
                      }}
                    >
                      Remove folder
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </Dropdown>
              {hasFolder && (
                <p
                  className="truncate text-xs text-neutral-500 dark:text-neutral-400"
                  title={folderName ?? undefined}
                >
                  Folder: {folderName ?? 'Selected folder'}
                </p>
              )}
              {shownRecords === 'folders' && (
                <FolderView
                  key={picked.target.externalId}
                  account={{ ref: picked.target, name: picked.displayName }}
                  selectedId={
                    filingTarget?.ref.kind === AmsRefKind.Folder
                      ? filingTarget.ref.externalId
                      : filingTarget?.folder?.externalId
                  }
                  onChoose={(folder) => {
                    if (filingTarget && filingTarget.ref.kind !== AmsRefKind.Folder) {
                      setFilingTarget({
                        ref: filingTarget.ref,
                        displayName: filingTarget.displayName,
                        ...(folder
                          ? {
                              folder: refInputOf(folder.ref),
                              folderDisplayName: folder.displayName
                            }
                          : {})
                      });
                    } else {
                      setFilingTarget(
                        folder
                          ? { ref: refInputOf(folder.ref), displayName: folder.displayName }
                          : null
                      );
                    }
                    setShownRecords(null);
                  }}
                />
              )}
              {shownRecords === 'activities' && (
                <ActivityView
                  key={picked.target.externalId}
                  account={{ ref: picked.target, name: picked.displayName }}
                  selectedId={
                    filingTarget?.ref.kind === AmsRefKind.Activity
                      ? filingTarget.ref.externalId
                      : undefined
                  }
                  onChoose={(activity) => {
                    const folder =
                      filingTarget?.ref.kind === AmsRefKind.Folder
                        ? { folder: filingTarget.ref, folderDisplayName: filingTarget.displayName }
                        : filingTarget?.folder
                          ? {
                              folder: filingTarget.folder,
                              folderDisplayName: filingTarget.folderDisplayName
                            }
                          : {};
                    setFilingTarget(
                      activity
                        ? {
                            ref: refInputOf(activity.ref),
                            displayName: activity.displayName,
                            ...folder
                          }
                        : null
                    );
                    setShownRecords(null);
                  }}
                />
              )}
              {shownRecords === 'policies' && (
                <div>
                  <AccountView
                    key={picked.target.externalId}
                    capability={{
                      integrationId: match.integrationId,
                      integrationType: match.integrationType,
                      displayName: amsName,
                      pickableKinds: match.filingKinds,
                      filesBrowsable: false
                    }}
                    account={{ ref: picked.target, name: picked.displayName }}
                    tab="policies"
                    onTab={() => {}}
                    filters={policyFilters}
                    onFilters={setPolicyFilters}
                    isSelected={(ref) => ref.externalId === filingTarget?.ref.externalId}
                    onToggle={(selection) => {
                      setFilingTarget({
                        ref: refInputOf(selection.ref),
                        displayName: selection.displayName,
                        ...(filingTarget?.ref.kind === AmsRefKind.Folder
                          ? {
                              folder: filingTarget.ref,
                              folderDisplayName: filingTarget.displayName
                            }
                          : filingTarget?.folder
                            ? {
                                folder: filingTarget.folder,
                                folderDisplayName: filingTarget.folderDisplayName
                              }
                            : {})
                      });
                      setShownRecords(null);
                    }}
                    recoveryHint="Try again, or use Client record."
                    onOpenPolicy={() => {}}
                  />
                </div>
              )}
            </div>
          )}
          {props.mode === 'compose' && props.onSkip && (
            <div>
              <button
                type="button"
                disabled={isChoosing}
                className="font-medium underline text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white disabled:opacity-50"
                onClick={() => {
                  props.onSkip?.();
                  props.onClose();
                }}
              >
                Don&apos;t log this email
              </button>
            </div>
          )}
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            {mode === 'compose'
              ? 'Cara logs the email to this client when it sends. You can change it until then.'
              : "Next, you'll confirm before anything is logged."}
          </span>
          {error && (
            <span role="alert" className="block text-red-600 dark:text-red-400">
              {error}
            </span>
          )}
        </div>
      </DialogBody>
      <DialogActions className="shrink-0">
        <Button color="white" onClick={props.onClose} disabled={isChoosing}>
          Cancel
        </Button>
        <Button disabled={!picked} loading={isChoosing} onClick={() => void choose()}>
          {mode === 'compose' ? 'Use this client' : 'Continue'}
        </Button>
      </DialogActions>
    </>
  );
  return props.contentOnly ? (
    content
  ) : (
    <MountableDialog onClose={close} size="xl" className="flex max-h-[calc(100dvh-4rem)] flex-col">
      {content}
    </MountableDialog>
  );
};

export const ChooseClientDialog = (
  props: React.ComponentProps<typeof ChooseClientDialogContent>
) => (
  <ChooseClientDialogContent
    key={JSON.stringify([
      props.match.integrationId,
      [...new Set(props.toAddresses.map((address) => address.trim().toLowerCase()))].sort()
    ])}
    {...props}
  />
);
